import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowUpRight,
  Play,
  Mic,
  Users,
  Eye,
  MessageSquare,
  Globe,
  Film,
  Youtube,
  Mail,
  Send,
} from "lucide-react";
import { techThingsInterviews } from "@/lib/portfolio";
import logoTechThings from "@/assets/logos/techthings.webp";
import techthingsBanner from "@/assets/work/techthings-episodes.webp";
import { YouTubeFacade } from "@/components/YouTubeFacade";
import { SparkleDivider } from "@/components/SparkleDivider";
import { LightSectionBackdrop } from "@/components/LightSectionBackdrop";

const DESCRIPTION =
  "Tech & Things interviews by Scott Cunningham (@scottcbusiness): 550+ episodes on blockchain, Web3, AI, privacy, and emerging tech.";

export const Route = createFileRoute("/media")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },

      { title: "Media | Tech & Things by Scott Cunningham" },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Media | Tech & Things, Interviews & Creator Work" },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: "https://scottcbusiness.com/media" },
      { name: "twitter:title", content: "Media | Tech & Things, Interviews & Creator Work" },
      { name: "twitter:description", content: DESCRIPTION },
      { property: "og:image", content: "https://i.ytimg.com/vi/4HziAHHVji4/maxresdefault.jpg" },
      { property: "og:image:alt", content: "The Tech & Things Show featured episode thumbnail" },
      { name: "twitter:image", content: "https://i.ytimg.com/vi/4HziAHHVji4/maxresdefault.jpg" },
      { name: "twitter:image:alt", content: "The Tech & Things Show featured episode thumbnail" },
    ],
    links: [{ rel: "canonical", href: "https://scottcbusiness.com/media" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "VideoObject",
          name: "How I Earned $40,000 Of Crypto",
          description:
            "Scott Cunningham walks through the blockchain social platforms he used to earn crypto, on The Tech & Things Show YouTube channel.",
          thumbnailUrl: [
            "https://i.ytimg.com/vi/4HziAHHVji4/maxresdefault.jpg",
            "https://i.ytimg.com/vi/4HziAHHVji4/hqdefault.jpg",
          ],
          uploadDate: "2022-11-03T21:04:50-07:00",
          embedUrl: "https://www.youtube.com/embed/4HziAHHVji4",
          contentUrl: "https://www.youtube.com/watch?v=4HziAHHVji4",
          author: { "@type": "Person", name: "Scott Cunningham", url: "https://scottcbusiness.com/about" },
        }),
      },
    ],
  }),
  component: MediaPage,
});

const CHANNEL_URL = "https://www.youtube.com/@scottcbusiness";

function ytId(url: string): string | null {
  const m = url.match(/(?:youtu\.be\/|v=)([\w-]{11})/);
  return m ? m[1] : null;
}
function ytThumb(url: string) {
  const id = ytId(url);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : null;
}

const FEATURED: { guest: string; title: string; url: string }[] = [
  {
    guest: "John McAfee",
    title: "Creator of McAfee Security Systems",
    url: "https://www.youtube.com/watch?v=SQoHQXgidRU",
  },
  {
    guest: "Andreas M. Antonopoulos",
    title: "Bitcoin educator, author of Mastering Bitcoin",
    url: "https://youtu.be/aVdmJ5Kmn3Y",
  },
  {
    guest: "Naomi Brockwell",
    title: "Privacy advocate, NBTV",
    url: "https://youtu.be/iTZkxJFHUXU",
  },
  {
    guest: "Jeff Pulver",
    title: "Pioneered VoIP, helped start Mozilla Firefox",
    url: "https://youtu.be/n8DHTCq98OQ",
  },
  {
    guest: "Chris (MMCrypto)",
    title: "Crypto analyst & creator",
    url: "https://youtu.be/znmTsTgcuNU",
  },
  {
    guest: "Ryan Taylor",
    title: "CEO, Dash Core Group",
    url: "https://youtu.be/k2HqTPtNMGY",
  },
];

const STATS: { v: string; l: string; Icon: typeof Eye }[] = [
  { v: "550+", l: "Episodes produced", Icon: Mic },
  { v: "156K+", l: "Followers across platforms", Icon: Users },
  { v: "1.06M+", l: "Video views", Icon: Play },
  { v: "448K+", l: "Engagements", Icon: MessageSquare },
  { v: "68K+", l: "Comments", Icon: MessageSquare },
  { v: "22.7M+", l: "Total impressions", Icon: Eye },
];

const TOPICS: { label: string; blurb: string; href: string; accent: string }[] = [
  {
    label: "Web3 Founders",
    blurb:
      "Long-form conversations with founders, executives, and protocol leads across Web3 and crypto.",
    href: techThingsInterviews.playlists[0],
    accent: "var(--color-electric)",
  },
  {
    label: "AI & Tech",
    blurb:
      "Builders, researchers, and operators shaping AI, privacy, cybersecurity, and emerging tech.",
    href: techThingsInterviews.playlists[1],
    accent: "var(--color-violet)",
  },
  {
    label: "Creators & Culture",
    blurb:
      "Independent creators, educators, and platforms redefining how the internet is built and used.",
    href: techThingsInterviews.playlists[2],
    accent: "var(--color-pink)",
  },
];

const CREDENTIALS = [
  "Hosted",
  "Researched",
  "Scripted",
  "Interviewed",
  "Produced",
  "Edited",
  "Distributed",
] as const;

function MediaPage() {
  const clips = (
    techThingsInterviews.groups[0].names as readonly (string | { name: string; url: string })[]
  )
    .filter((n): n is { name: string; url: string } => typeof n === "object" && "url" in n)
    .slice(0, 9);

  return (
    <>
      {/* Hero */}
      <section className="relative isolate overflow-hidden">
        <LightSectionBackdrop variant="blueprint" arcs={false} />
        <div className="absolute inset-0 -z-10 opacity-60">
          <div className="absolute -left-24 top-10 h-72 w-72 rounded-full bg-[var(--color-violet)] blur-3xl opacity-30" />
          <div className="absolute right-0 bottom-0 h-80 w-80 rounded-full bg-[var(--color-electric)] blur-3xl opacity-25" />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="max-w-5xl">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
              Tech &amp; Things • Creative Work
            </p>
            <h1 className="mt-4 font-display text-3xl md:text-4xl">
              Not just <em className="italic text-gradient">someone who posts</em>.{" "}
              <span className="xl:whitespace-nowrap">
                A long-form media operator with the receipts.
              </span>
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-relaxed text-muted-foreground md:text-lg">
              Tech &amp; Things is a 550+ episode podcast and creator platform covering blockchain,
              Web3, decentralization, AI, privacy, cybersecurity, and the internet's edges. YouTube
              is for watching. This page is for context: curated episodes, interviews, and topic
              playlists.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3 pb-2">
              <a
                href={CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="gradient-shift group inline-flex shrink-0 items-center gap-2 rounded-md border-2 border-foreground px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-background shadow-pop transition-transform hover:-translate-y-0.5"
              >
                <Youtube className="h-4 w-4" /> Watch on YouTube
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
              <Link
                to="/contact"
                className="group inline-flex shrink-0 items-center gap-2 rounded-md border-2 border-foreground bg-background px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-foreground shadow-pop transition-all hover:-translate-y-0.5 hover:bg-[var(--color-violet)] hover:text-background hover:border-[var(--color-violet)]"
              >
                <Send className="h-4 w-4" /> Invite me / collaborate
              </Link>
              <Link
                to="/newsletter"
                className="group inline-flex shrink-0 items-center gap-2 rounded-md border-2 border-foreground bg-background px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-foreground shadow-pop transition-all hover:-translate-y-0.5 hover:bg-[var(--color-electric)] hover:text-background hover:border-[var(--color-electric)]"
              >
                <Mail className="h-4 w-4" /> Subscribe to newsletter
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-2">
              {CREDENTIALS.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center rounded-full border border-foreground/30 bg-background/60 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground"
                >
                  {c}
                </span>
              ))}
            </div>
          </div>
          <figure className="group relative mx-auto mt-10 max-w-4xl overflow-hidden rounded-md border-2 border-foreground bg-background shadow-pop transition-transform hover:-translate-y-0.5">
            <span
              aria-hidden
              className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-[3px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background:
                  "linear-gradient(90deg, var(--color-violet), var(--color-pink) 50%, var(--color-electric))",
              }}
            />
            <span
              aria-hidden
              className="pointer-events-none absolute -inset-[2px] z-10 rounded-md opacity-0 transition-opacity duration-300 group-hover:opacity-100"
              style={{
                background:
                  "linear-gradient(135deg, var(--color-violet), var(--color-pink) 50%, var(--color-electric))",
                WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                WebkitMaskComposite: "xor",
                maskComposite: "exclude",
                padding: "2px",
              }}
            />
            <div className="relative aspect-video w-full">
              <YouTubeFacade
                videoId="4HziAHHVji4"
                title="Tech & Things featured episode"
                params="si=g-StNMWiITZSEAiD"
              />
            </div>
          </figure>
        </div>
      </section>

      <SparkleDivider />

      {/* Stats */}
      <section className="relative isolate overflow-hidden border-b-2 border-foreground bg-muted/30">
        <LightSectionBackdrop variant="analytics" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                Creator stats
              </p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">By the numbers</h2>
            </div>
            <p className="hidden max-w-sm text-sm text-muted-foreground md:block">
              Six years of continuous publishing across 50+ distribution platforms.
            </p>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {STATS.map(({ v, l, Icon }) => (
              <div
                key={l}
                className="group relative flex items-start gap-4 rounded-md border-2 border-foreground bg-background p-6 shadow-pop transition-all hover:-translate-y-0.5"
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      "linear-gradient(90deg, var(--color-violet), var(--color-pink) 50%, var(--color-electric))",
                  }}
                />
                <span
                  aria-hidden
                  className="pointer-events-none absolute -inset-[2px] rounded-md opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                  style={{
                    background:
                      "linear-gradient(135deg, var(--color-violet), var(--color-pink) 50%, var(--color-electric))",
                    WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    maskComposite: "exclude",
                    padding: "2px",
                  }}
                />
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border-2 border-foreground bg-background text-foreground transition-colors group-hover:bg-foreground group-hover:text-background">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <p className="font-display text-3xl leading-none md:text-4xl text-gradient">
                    {v}
                  </p>
                  <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
                    {l}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured episodes */}
      <section className="relative isolate overflow-hidden border-b-2 border-foreground">
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                Featured episodes
              </p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">
                Conversations that <em className="italic text-gradient">moved the needle</em>
              </h2>
            </div>
            <a
              href={CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground md:inline-flex"
            >
              Full archive on YouTube <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURED.map((ep) => {
              const thumb = ytThumb(ep.url);
              return (
                <a
                  key={ep.url}
                  href={ep.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group block overflow-hidden rounded-md border-2 border-foreground bg-background shadow-pop transition-transform hover:-translate-y-1"
                >
                  <div className="relative aspect-video overflow-hidden border-b-2 border-foreground bg-muted">
                    {thumb && (
                      <img
                        src={thumb}
                        alt={`Tech & Things episode with ${ep.guest}`}
                        loading="lazy"
                        width={480}
                        height={270}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.04]"
                      />
                    )}
                    <span className="absolute inset-0 grid place-items-center bg-foreground/0 transition-colors group-hover:bg-foreground/30">
                      <span className="grid h-14 w-14 place-items-center rounded-full border-2 border-background bg-foreground text-background opacity-0 transition-opacity group-hover:opacity-100">
                        <Play className="h-5 w-5 translate-x-0.5" />
                      </span>
                    </span>
                  </div>
                  <div className="p-5">
                    <p className="font-display text-lg leading-snug text-foreground group-hover:text-gradient">
                      {ep.guest}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{ep.title}</p>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Topic categories */}
      <section className="relative isolate overflow-hidden border-b-2 border-foreground bg-muted/30">
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Topic categories
          </p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl">Three playlists, one archive</h2>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {TOPICS.map((t) => (
              <a
                key={t.href}
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative overflow-hidden rounded-md border-2 border-foreground bg-background p-6 shadow-pop transition-transform duration-300 hover:-translate-y-1 hover:scale-[1.03]"
              >
                <span
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1"
                  style={{ background: t.accent }}
                />
                <Globe className="h-6 w-6 text-foreground" />
                <h3 className="card-header-fx mt-4 font-display text-xl">{t.label}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t.blurb}</p>
                <span className="mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground transition-all group-hover:text-foreground group-hover:font-bold">
                  Open playlist{" "}
                  <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:rotate-45" />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Best interview clips */}
      <section className="relative isolate overflow-hidden border-b-2 border-foreground">
        <LightSectionBackdrop variant="analytics" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
                Best interview clips
              </p>
              <h2 className="mt-2 font-display text-3xl md:text-4xl">Hand-picked highlights</h2>
            </div>
            <Link
              to="/work/$slug"
              params={{ slug: "tech-and-things" }}
              className="hidden items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground md:inline-flex"
            >
              Full guest list <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {clips.map((c) => {
              const thumb = ytThumb(c.url);
              return (
                <a
                  key={c.url + c.name}
                  href={c.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center gap-4 rounded-md border-2 border-foreground bg-background p-3 shadow-pop transition-transform hover:-translate-y-0.5"
                >
                  <div className="relative aspect-video w-32 shrink-0 overflow-hidden rounded-sm border-2 border-foreground bg-muted">
                    {thumb && (
                      <img
                        src={thumb}
                        alt={`Clip with ${c.name}`}
                        loading="lazy"
                        width={270}
                        height={480}
                        className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.06]"
                      />
                    )}
                    <span className="absolute inset-0 grid place-items-center">
                      <span className="grid h-8 w-8 place-items-center rounded-full border-2 border-background bg-foreground text-background opacity-0 transition-opacity group-hover:opacity-100">
                        <Play className="h-3.5 w-3.5 translate-x-0.5" />
                      </span>
                    </span>
                  </div>
                  <p className="font-mono text-[12px] leading-snug text-foreground group-hover:text-gradient">
                    {c.name}
                  </p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partnerships / recognition */}
      <section className="relative isolate overflow-hidden border-b-2 border-foreground bg-muted/30">
        <LightSectionBackdrop variant="dots" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted-foreground">
            Recognition &amp; partnerships
          </p>
          <h2 className="mt-2 font-display text-3xl md:text-4xl">
            Hosted, interviewed, and worked alongside
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="group overflow-hidden rounded-md border-2 border-foreground bg-background p-4 shadow-pop transition-transform duration-300 hover:scale-[1.03] md:mr-8 md:hover:scale-100">
                {/* Mobile/tablet: show banner image only */}
                <div className="flex items-center justify-center md:hidden">
                  <img
                    src={techthingsBanner}
                    alt="Tech & Things podcast banner"
                    className="h-auto w-full object-contain"
                    loading="lazy"
                    width={1200}
                    height={400}
                  />
                </div>
                {/* md and up: original logo + captions */}
                <div className="hidden items-center justify-center md:flex">
                  <img
                    src={logoTechThings}
                    alt="Tech & Things logo"
                    className="h-auto w-full max-h-44 object-contain transition-transform duration-300 group-hover:scale-105 md:max-h-56"
                    loading="lazy"
                    width={600}
                    height={600}
                  />
                </div>
                <p className="mt-6 hidden text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground md:block">
                  550 episodes &amp; 100+ guests
                </p>
                <p className="mt-1 hidden text-center font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground md:block">
                  Distributed to 50+ platforms
                </p>
              </div>
            </div>
            <div className="md:col-span-7">
              <ul className="grid gap-3 font-mono text-[12px] leading-snug text-muted-foreground sm:grid-cols-2">
                {[
                  {
                    name: "John McAfee",
                    role: "Creator of McAfee Security Systems",
                    url: "https://www.youtube.com/watch?v=SQoHQXgidRU",
                    featured: true,
                  },
                  { name: "Andreas M. Antonopoulos", role: "Bitcoin educator & author" },
                  { name: "Naomi Brockwell", role: "Privacy advocate, NBTV" },
                  { name: "Jeff Pulver", role: "Pioneer of VoIP, Mozilla Firefox" },
                  { name: "Bradley Kam", role: "Unstoppable Domains" },
                  { name: "Luke Mulks", role: "Brave Browser" },
                  { name: "Colin Pape", role: "Presearch" },
                  { name: "Rob Viglione", role: "Horizen" },
                  { name: "Bill Ottman", role: "Minds.com" },
                  { name: "Ryan Taylor", role: "Dash Core Group" },
                  { name: "Mark Van Rijmenam", role: "Datafloq" },
                  { name: "Erica Stanford", role: "Crypto Wars" },
                ].map((g) => (
                  <li key={g.name} className="flex items-start gap-2">
                    <Film className="mt-0.5 h-3.5 w-3.5 shrink-0 text-foreground" />
                    <span>
                      {"url" in g && g.url ? (
                        <a
                          href={g.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover-text-gradient text-foreground no-underline"
                        >
                          {g.name}
                        </a>
                      ) : (
                        <span className="text-foreground">{g.name}</span>
                      )}
                      {"featured" in g && g.featured && (
                        <span className="ml-2 inline-flex items-center rounded-full border border-foreground/40 bg-background px-1.5 py-0.5 align-middle font-mono text-[9px] uppercase tracking-[0.2em] text-gradient">
                          Featured
                        </span>
                      )}
                      <span className="block text-[11px] text-muted-foreground">{g.role}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <Link
                to="/work/$slug"
                params={{ slug: "tech-and-things" }}
                className="group mt-6 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
              >
                <span className="sheen underline-offset-4 group-hover:underline">
                  See the full guest list on Work
                </span>
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTAs */}
      <section className="relative isolate overflow-hidden">
        <LightSectionBackdrop variant="blueprint" arcs={false} sparkles={false} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-6 md:grid-cols-3">
            <a
              href={CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="gradient-shift group flex flex-col justify-between rounded-md border-2 border-foreground p-8 text-background shadow-pop transition-transform hover:-translate-y-1"
            >
              <div>
                <Youtube className="h-6 w-6" />
                <h3 className="mt-4 font-display text-2xl">Watch on YouTube</h3>
                <p className="mt-3 text-sm leading-relaxed text-background/80">
                  The full archive: 550+ episodes, three curated playlists, six years of long-form
                  conversations.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em]">
                Open channel{" "}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </a>
            <Link
              to="/contact"
              className="group flex flex-col justify-between rounded-md border-2 border-foreground bg-background p-8 shadow-pop transition-transform hover:-translate-y-1"
              style={{ boxShadow: "8px 8px 0 var(--color-violet)" }}
            >
              <div>
                <Send className="h-6 w-6" />
                <h3 className="mt-4 font-display text-2xl">Invite me / collaborate</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Available for guesting, hosting, interviews, podcast appearances, or producing a
                  show with your team. Let's talk.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-foreground">
                Get in touch{" "}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
            <Link
              to="/newsletter"
              className="group flex flex-col justify-between rounded-md border-2 border-foreground bg-background p-8 shadow-pop transition-transform hover:-translate-y-1"
              style={{ boxShadow: "8px 8px 0 var(--color-electric)" }}
            >
              <div>
                <Mail className="h-6 w-6" />
                <h3 className="mt-4 font-display text-2xl">Subscribe to the newsletter</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  Monthly field notes on AI, Web3, privacy, decentralization, and the systems
                  shaping the internet.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-foreground">
                Read the Digital Debrief{" "}
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
